import React, { useEffect, useMemo, useRef, useState } from 'react';
import StatsChartComponent from './charts/StatsChartComponent';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import LineChartComponent from './charts/LineChartComponent';
import PieChartComponent from './charts/PieChartComponent';
import BarChartComponent from './charts/BarChartComponent';
import TextChartComponent from './charts/TextChartComponent';
import ImageChartComponent from './charts/ImageChartComponent';
import { DEFAULT_NUMBER_OF_COLUMNS, getMaxTakenRows, WIDGET_LOAD_CHUNK_SIZE } from './utils';
import { CallAPI } from 'actions/General';
import { useSocket } from 'hooks/useSocket';
import ConfirmationModal from 'components/common-components/ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DialChartComponent from './charts/DialChartComponent';
import ChartHeader from './charts/ChartHeader';
import HeatMapChartComponent from './charts/HeatMapChartComponent';

const PageWidgetsView = ({ companyId, selectedPageId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [widgetList, setWidgetList] = useState([]);
  const [remainingWidgetList, setRemainingWidgetList] = useState([]);
  const [fetchWidgetList, setFetchWidgetList] = useState([]);
  const [hasAccessToPage, setHasAccessToPage] = useState(true);
  const [pageDeleted, setPageDeleted] = useState(false);
  const [dimensionsLookup, setDimensionsLookup] = useState({});
  const isMounted = useRef(true);
  const totalOccupiedCells = useMemo(
    () => widgetList.reduce((acc, cur) => acc + cur.takeRows * cur.takeColumns, 0),
    [widgetList],
  );
  const currentWidgetsRows = useMemo(() => {
    return getMaxTakenRows(widgetList);
  }, [widgetList.length]);
  const awaitTimeout = (delay = 2000) => new Promise((resolve) => setTimeout(resolve, delay));

  const getAnalyticsDetail = async () => {
    const result = await CallAPI(
      'GET_PAGE_ANALYTICS_DETAIL',
      { pageId: selectedPageId },
      null,
      null,
      null,
    );
    if (result.status) {
      const simpleWidgets = [];
      const complexWidgets = [];
      result.data.forEach((widget) => {
        simpleWidgets.push(widget);
        if (!['statsCard', 'textCard', 'imageCard'].includes(widget.basicProps.chartType))
          complexWidgets.push(widget);
      });

      if (isMounted.current) {
        setWidgetList(simpleWidgets);
        setFetchWidgetList(complexWidgets);
      }
    }
  };

  const getUPdatedStatsOfWidget = (widget) => {
    let mapX = false;
    let mapY = false;

    widget.basicProps.dimensions.forEach((dim, index) => {
      if (
        ['companyFields', 'formSets', 'questionSets'].includes(dim.type) &&
        dim.optionDetails.selectedOption.length > 1
      ) {
        if (index === 0) mapX = true;
        if (index === 1) mapY = true;
      }
    });
    if (!mapX && !mapY) return widget;
    return {
      ...widget,
      stats: widget.stats.map((stat) => ({
        ...stat,
        ...(mapX && stat.xaxis && { xaxis: dimensionsLookup[stat.xaxis] ?? stat.xaxis }),
        ...(mapY && stat.yaxis && { yaxis: dimensionsLookup[stat.yaxis] ?? stat.yaxis }),
      })),
    };
  };

  const getWidgetsChunkData = async () => {
    if (!remainingWidgetList.length) return;
    const dataResult = await CallAPI(
      'GET_WIDGETS_ANALYTICS_DATA',
      remainingWidgetList,
      null,
      null,
      null,
    );

    if (isMounted.current) {
      const dataMap = Object.fromEntries(dataResult.data.map((d) => [d._id, d]));
      setWidgetList((prevList) =>
        prevList.map((item) => {
          const foundWidget = dataMap[item._id];
          if (!foundWidget) return item;
          if (foundWidget.basicProps?.dimensions?.length) {
            return getUPdatedStatsOfWidget({ ...foundWidget });
          }
          return foundWidget;
        }),
      );
    }
  };

  const actionOnWebHook = async (data) => {
    if (data.type === 'ANALYSIS_UPDATED') {
      setFetchWidgetList(cloneDeep(widgetList));
    } else if (data.type === 'WIDGET_UPDATED') {
      const foundWidget = widgetList.find((w) => w._id === data.widgetId);
      if (foundWidget) {
        setFetchWidgetList((prevList) => [...prevList, cloneDeep(foundWidget)]);
      }
    } else if (data.type === 'WIDGET_REMOVED') {
      console.log(data);
      setWidgetList((prevWidgets) => prevWidgets.filter((item) => item._id !== data.widgetId));
    } else if (data.type === 'WIDGET_ADDED') {
      console.log(data);
      setWidgetList((prevList) => [...prevList, { ...data.widgetDetails }]);
      setFetchWidgetList((prevList) => [...prevList, { ...data.widgetDetails }]);
    } else if (data.type === 'WIDGET_POSITION_UPDATED') {
      console.log(data);
      setWidgetList((prevWidgets) => {
        const updateMap = new Map(data.widgetDetails.map((item) => [item.widgetId, item]));
        return prevWidgets.map((widget) =>
          updateMap.has(widget._id) ? { ...widget, ...updateMap.get(widget._id) } : widget,
        );
      });
    } else if (data.type === 'CHECK_ACCESS') {
      console.log(data);
      const result = await CallAPI('CHECK_PAGE_ACCESS', null, selectedPageId, null, null);
      if (!result.status) {
        setHasAccessToPage(false);
      }
    } else if (data.type === 'PAGE_DELETED') {
      setPageDeleted(true);
    }
  };

  const fetchRemainingWidgetsData = async () => {
    if (!fetchWidgetList.length) return;
    const list = cloneDeep(fetchWidgetList);
    const widgetsChunk = list.slice(0, WIDGET_LOAD_CHUNK_SIZE);
    const wIds = widgetsChunk.map((w) => w._id);

    setRemainingWidgetList(widgetsChunk);
    await awaitTimeout(1000);
    setFetchWidgetList((prevList) => prevList.filter((w) => !wIds.includes(w._id)));
  };

  const fetchDimensionsOptions = async () => {
    const result = await CallAPI('GET_DASHBOARD_WIDGET_DIMENSIONS_OPTIONS', {});

    if (result.status) {
      const dimensionsLookupObject = {};
      result.data.companyFieldsList.forEach((field) => {
        dimensionsLookupObject[field._id] = field.fieldTitle;
      });

      result.data.formSets.forEach((set) => {
        dimensionsLookupObject[set._id] = set.title;
      });

      result.data.questionSets.forEach((set) => {
        dimensionsLookupObject[set._id] = set.title;
      });

      result.data.formsList.forEach((form) => {
        dimensionsLookupObject[form._id] = form.title;
      });
      setDimensionsLookup(cloneDeep(dimensionsLookupObject));
    }
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (remainingWidgetList.length) getWidgetsChunkData();
  }, [remainingWidgetList]);

  useEffect(() => {
    if (fetchWidgetList.length) fetchRemainingWidgetsData();
  }, [fetchWidgetList]);

  useEffect(() => {
    const init = async () => {
      await fetchDimensionsOptions();
      if (selectedPageId) {
        getAnalyticsDetail();
      }
    };

    init();
  }, [selectedPageId]);

  useSocket(companyId, selectedPageId, actionOnWebHook);

  return (
    <div
      className='widgets-grid-view'
      style={{ '--columns': DEFAULT_NUMBER_OF_COLUMNS, '--rows': currentWidgetsRows }}
    >
      {Array.from({
        length: DEFAULT_NUMBER_OF_COLUMNS * currentWidgetsRows - totalOccupiedCells,
      }).map((_, i) => (
        <div key={`cell-${i}`} className='grid-cell' />
      ))}
      {widgetList.map((widget, index) => (
        <div
          key={index}
          className='widget'
          style={{
            gridColumn: `${widget.startColumn} / span ${widget.takeColumns}`,
            gridRow: `${widget.startRow} / span ${widget.takeRows}`,
          }}
        >
          <ChartHeader widget={widget} />
          {widget.basicProps.chartType === 'statsCard' ? (
            <StatsChartComponent widget={widget} data={widget.stats} />
          ) : widget.basicProps.chartType === 'textCard' ? (
            <TextChartComponent widget={widget} />
          ) : widget.basicProps.chartType === 'imageCard' ? (
            <ImageChartComponent widget={widget} />
          ) : widget.basicProps.chartType === 'lineChart' ? (
            <LineChartComponent widget={widget} stats={widget.stats} />
          ) : widget.basicProps.chartType === 'barChartHorizontal' ||
            widget.basicProps.chartType === 'barChartVertical' ||
            widget.basicProps.chartType === 'stackedBarChartHorizontal' ||
            widget.basicProps.chartType === 'stackedBarChartVertical' ? (
            <BarChartComponent
              widget={widget}
              stats={widget.stats}
              isVertical={
                widget.basicProps.chartType === 'barChartVertical' ||
                widget.basicProps.chartType === 'stackedBarChartVertical'
              }
            />
          ) : widget.basicProps.chartType === 'heatMapTable' ? (
            <HeatMapChartComponent widget={widget} stats={widget.stats} />
          ) : widget.basicProps.chartType === 'dialChart' ? (
            <DialChartComponent
              widget={widget}
              stats={widget.stats}
              chartType={widget.basicProps.chartType}
            />
          ) : widget.basicProps.chartType === 'pieChart' ||
            widget.basicProps.chartType === 'doughnutChart' ? (
            <PieChartComponent
              widget={widget}
              stats={widget.stats}
              chartType={widget.basicProps.chartType}
            />
          ) : (
            <div>
              <label>{widget.basicProps.chartType}</label>
            </div>
          )}
        </div>
      ))}
      {!hasAccessToPage && (
        <ConfirmationModal
          show={true}
          title={t('alert_dashboard_page_no_access')}
          message={t('alert_dashboard_page_no_access_message')}
          actionList={[
            { color: 'red-1', text: t('button_ok'), onClick: () => navigate('/dashboards') },
          ]}
        />
      )}
      {pageDeleted && (
        <ConfirmationModal
          show={true}
          title={t('alert_dashboard_page_deleted')}
          message={t('alert_dashboard_page_deleted_message')}
          actionList={[
            { color: 'red-1', text: t('button_ok'), onClick: () => navigate('/dashboards') },
          ]}
        />
      )}
    </div>
  );
};

PageWidgetsView.propTypes = {
  selectedPageId: PropTypes.string,
  companyId: PropTypes.string.isRequired,
};

export default PageWidgetsView;
